// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { withRole } from '@common/authorization/Roles';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '@errorHandler/errors';
import { Button } from '@imprivata-cloud/components';
import {
  Button as ButtonDS,
  type DateRange,
  DateRangePicker,
} from '@imprivata-cloud/design-system';
import { useLoginActivityReport } from '@portal/containers/identityUsers/hooks/useReport';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { downloadAsFileToUserComputer, lastXDaysDateRange, toIsoDateString } from '@/utils/utils';
import DownloadImg from '../../../assets/icons/download.svg?react';
import { ROLE_TYPE_ADMIN } from '../../constants';
import './ActivityReport.less';

const TRANSLATION = 'portal.activity-report';
const MAX_DAYS = 30;
const DOWNLOAD_FILE_NAME = 'login-activity-report.csv';

function LoginActivityReport() {
  const { t } = useTranslation();
  const { emitError } = useErrorContext();

  const [range, setRange] = useState<DateRange>(lastXDaysDateRange(MAX_DAYS));

  const { mutateAsync: getActivityReport, isPending } = useLoginActivityReport();

  const handleDownload = async () => {
    try {
      const data = await getActivityReport({
        startDate: toIsoDateString(range?.from),
        endDate: toIsoDateString(range?.to),
      });
      if (!data) {
        return;
      }
      downloadAsFileToUserComputer(data, DOWNLOAD_FILE_NAME);
    } catch (e) {
      emitError(new AppError(e as ApiError));
    }
  };

  return (
    <div className="activity-report">
      <p>{t(`${TRANSLATION}.login.description`)}</p>
      <DateRangePicker
        className="date-range-picker"
        label={t(`${TRANSLATION}.date-range-label`)}
        subLabel={t(`${TRANSLATION}.date-range-subscript`)}
        selected={range}
        onSelect={setRange}
        calendarProps={{ mode: 'range', disabled: { after: new Date() }, max: MAX_DAYS - 1 }}
        footerActions={
          <div className="date-range-footer flex gap-1 p-1">
            {[7, 14, MAX_DAYS].map(days => (
              <ButtonDS
                key={days}
                variant="link"
                size="sm"
                onClick={() => setRange(lastXDaysDateRange(days))}
              >
                {t(`${TRANSLATION}.date-range-value`, { count: days })}
              </ButtonDS>
            ))}
          </div>
        }
      />
      <Button
        type="primary"
        disabled={isPending || !range?.from || !range?.to}
        size="large"
        icon={<DownloadImg />}
        onClick={handleDownload}
      >
        {t(`${TRANSLATION}.download-button`)}
      </Button>
    </div>
  );
}

export default withRole({ authorizedRoles: [ROLE_TYPE_ADMIN] })(LoginActivityReport);
